// What's new: pure helpers (no React Native imports, so jest and the
// newsletter builder can load them). Rules and the routine: docs/whats-new.md.

// Where links may point. Every entry links to an article on eventlive.pro.
const LINK_HOSTS = ["www.eventlive.pro", "eventlive.pro"];
// "Try it" buttons that open a page outside the app.
const TRY_URL_HOSTS = [...LINK_HOSTS, "apps.apple.com", "play.google.com"];

// In-app "Try it" actions. The app maps each one to a screen
// (components/whatsNew/actions.js); entries with an action this build doesn't
// know (a newer feed on an older app) just don't show the button.
export const TRY_ACTIONS = [
  "createEvent",
  "createFundraiser",
  "startTest",
  "openSupport",
  "videoWall",
  "plans",
  "downloadEverything",
  "team",
];

export const PLATFORMS = ["web", "ios", "android"];

// The newest entries a broadcaster sees. Older ones drop off the list.
export const MAX_ENTRIES = 20;

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const ID = /^[a-z0-9][a-z0-9-]{2,63}$/;

const hostOf = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "https:" ? u.hostname : null;
  } catch {
    return null;
  }
};

const isLink = (url, hosts) => hosts.includes(hostOf(url));

// The entry, cleaned up, or null when it can't be shown safely. The feed can
// come from the network (native apps fetch the latest one), so nothing in it
// is trusted: unknown fields are dropped and links must stay on our hosts.
export function normalizeEntry(raw) {
  if (!raw || typeof raw !== "object") return null;
  const { id, date, title, body } = raw;
  if (typeof id !== "string" || !ID.test(id)) return null;
  if (typeof date !== "string" || !DATE.test(date)) return null;
  if (typeof title !== "string" || !title.trim()) return null;
  if (typeof body !== "string" || !body.trim()) return null;
  const link = raw.link;
  if (
    !link ||
    typeof link.label !== "string" ||
    !isLink(link.url, LINK_HOSTS)
  ) {
    return null;
  }
  const entry = {
    id,
    date,
    title: title.trim(),
    body: body.trim(),
    link: { label: link.label, url: link.url },
  };
  if (typeof raw.tag === "string" && raw.tag.trim()) entry.tag = raw.tag.trim();
  const t = raw.try;
  if (t && typeof t.label === "string" && t.label.trim()) {
    if (TRY_ACTIONS.includes(t.action)) {
      entry.try = { label: t.label.trim(), action: t.action };
    } else if (!t.action && isLink(t.url, TRY_URL_HOSTS)) {
      entry.try = { label: t.label.trim(), url: t.url };
    } else if (typeof t.action === "string") {
      entry.try = { label: t.label.trim(), action: t.action, unknown: true };
    }
  }
  if (Array.isArray(raw.roles) && raw.roles.length) {
    entry.roles = raw.roles.filter((r) => typeof r === "string");
  }
  if (Array.isArray(raw.platforms) && raw.platforms.length) {
    entry.platforms = raw.platforms.filter((p) => PLATFORMS.includes(p));
  }
  if (raw.min_version && typeof raw.min_version === "object") {
    const min = {};
    for (const p of ["ios", "android"]) {
      if (typeof raw.min_version[p] === "string") min[p] = raw.min_version[p];
    }
    if (Object.keys(min).length) entry.min_version = min;
  }
  if (typeof raw.until === "string" && DATE.test(raw.until)) {
    entry.until = raw.until;
  }
  return entry;
}

export function normalizeFeed(feed) {
  const list = Array.isArray(feed) ? feed : feed?.entries;
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    const entry = normalizeEntry(raw);
    if (!entry || seen.has(entry.id)) continue;
    seen.add(entry.id);
    out.push(entry);
  }
  return out;
}

// -1, 0 or 1. "4.3.10" > "4.3.9"; missing parts count as 0.
export function compareVersions(a, b) {
  const pa = String(a || "0").split(".").map((n) => parseInt(n, 10) || 0);
  const pb = String(b || "0").split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d > 0 ? 1 : -1;
  }
  return 0;
}

const today = (now) => new Date(now).toISOString().slice(0, 10);

// The entries one broadcaster sees, newest first. An entry can be written
// ahead of time: it appears on its date. `until` hides it after that day
// (e.g. an offer that ends).
export function entriesFor(
  entries,
  { role, platform = "web", appVersion, now = Date.now() } = {}
) {
  const day = today(now);
  return entries
    .filter((e) => e.date <= day)
    .filter((e) => !e.until || e.until >= day)
    .filter((e) => !e.roles || (role && e.roles.includes(role)))
    .filter((e) => !e.platforms || e.platforms.includes(platform))
    .filter((e) => {
      const min = e.min_version?.[platform];
      return !min || !appVersion || compareVersions(appVersion, min) >= 0;
    })
    .sort((a, b) => (a.date === b.date ? 0 : a.date < b.date ? 1 : -1))
    .slice(0, MAX_ENTRIES);
}

// Ids the broadcaster hasn't seen yet. Entries from before the account was
// created aren't news to it: they're listed, but not highlighted.
export function unreadIds(entries, seenIds, registeredDatetime) {
  const seen = new Set(seenIds || []);
  const since =
    typeof registeredDatetime === "string" && DATE.test(registeredDatetime.slice(0, 10))
      ? registeredDatetime.slice(0, 10)
      : null;
  return entries
    .filter((e) => !seen.has(e.id))
    .filter((e) => !since || e.date >= since)
    .map((e) => e.id);
}

// What to keep in storage: ids still in the feed come first, then a few older
// ones (a remote feed may drop an entry the bundled one still has).
export function mergeSeen(previous, ids, feedIds, limit = 200) {
  const all = [...new Set([...(previous || []), ...(ids || [])])];
  const current = new Set(feedIds || []);
  const inFeed = all.filter((id) => current.has(id));
  const rest = all.filter((id) => !current.has(id));
  return [...inFeed, ...rest].slice(0, limit);
}

// Links from the app carry where they were opened from, so eventlive.pro's
// analytics can count them.
export function trackedUrl(url, { id, source = "app" } = {}) {
  try {
    const u = new URL(url);
    if (!u.searchParams.has("utm_source")) {
      u.searchParams.set("utm_source", "broadcaster-app");
      u.searchParams.set("utm_medium", "whats-new");
      u.searchParams.set("utm_campaign", "whats-new");
      if (id) u.searchParams.set("utm_content", id);
      if (source) u.searchParams.set("utm_term", source);
    }
    return u.toString();
  } catch {
    return url;
  }
}

// "Oct 13" (this year) or "Oct 13, 2025".
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function formatEntryDate(date, now = Date.now()) {
  const [y, m, d] = date.split("-").map((n) => parseInt(n, 10));
  const label = `${MONTHS[m - 1]} ${d}`;
  return y === new Date(now).getUTCFullYear() ? label : `${label}, ${y}`;
}
